export type PersistentStoragePermissionState = "never" | "prompt" | "persisted" | "denied";

export type PersistentStorageQuotaEstimate = {
	readonly quota: number;
	readonly usage: number;
};

export async function isStoragePersisted(): Promise<boolean | undefined> {
	if (!navigator.storage?.persisted) {
		return undefined;
	}
	return navigator.storage.persisted();
}

export async function persistStorage(): Promise<boolean | undefined> {
	if (!navigator.storage?.persist) {
		return undefined;
	}
	return navigator.storage.persist();
}

export async function estimatePersistentStorageQuota(): Promise<PersistentStorageQuotaEstimate | undefined> {
	if (!navigator.storage?.estimate) {
		return undefined;
	}
	const estimate = await navigator.storage.estimate();
	if (estimate && typeof estimate.quota === "number" && typeof estimate.usage === "number") {
		return { quota: estimate.quota, usage: estimate.usage };
	}
	return undefined;
}

export async function requestPersistentStorageWithoutPrompt(): Promise<PersistentStoragePermissionState> {
	if (!navigator.storage?.persisted) {
		return "never";
	}

	let persisted = await navigator.storage.persisted();
	if (persisted) {
		return "persisted";
	}

	if (!navigator.permissions?.query) {
		return "prompt";
	}

	let permission: PermissionStatus;
	try {
		permission = await navigator.permissions.query({
			name: "persistent-storage" as PermissionName
		});
	} catch (error) {
		console.warn("failed to query permissions, returning prompt", error);
		return "prompt";
	}

	if (permission.state === "granted") {
		persisted = await navigator.storage.persist?.();
		if (persisted) {
			return "persisted";
		}
		// The permission was already granted yet the browser still refused to persist — a genuine
		// denial (the exact eviction risk this guards), not an unexpected error. Return it as "denied"
		// so callers classify it as a denial instead of bucketing it under generic error telemetry.
		return "denied";
	}

	if (permission.state === "prompt") {
		return "prompt";
	}

	return "never";
}
